/**
 * Shared pieces of the options-simulator UI: stat tiles, chart frames, the ranked
 * alternatives table (Analyse drawer + replay decisions) and the series palette.
 * Colours come from styles/colorTokens (chartSeries is validated for CVD separation).
 */
import React from 'react';
import {
  Box, Card, CardContent, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { ResponsiveContainer } from 'recharts';
import { useAppTheme } from '../../hooks/useAppTheme';
import type { SimAltRow } from '../../services/optionsim.service';
import { money, num, pct, signedMoney } from './simFormat';

/** Phone layout below 600 px (MUI `sm`). */
export const useIsPhone = (): boolean => useMediaQuery('(max-width:599.95px)');

export const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string; tone?: 'bull' | 'bear' | 'warn' | null; testId?: string }> = ({ label, value, hint, tone, testId }) => {
  const { C } = useAppTheme();
  const color = tone === 'bull' ? C.bull : tone === 'bear' ? C.bear : tone === 'warn' ? C.warn : 'text.primary';
  const body = (
    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', minWidth: 0 }} data-testid={testId}>
      <Typography variant="caption" color="text.secondary" noWrap component="div">{label}</Typography>
      <Typography variant="body2" fontWeight={800} sx={{ color, fontVariantNumeric: 'tabular-nums' }} noWrap component="div">{value}</Typography>
    </Box>
  );
  return hint ? <Tooltip title={hint} arrow>{body}</Tooltip> : body;
};

/** Tiles in a responsive grid: 2 columns on a phone, auto-fill above. */
export const StatGrid: React.FC<{ children: React.ReactNode; min?: number }> = ({ children, min = 132 }) => (
  <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: `repeat(auto-fill, minmax(${min}px, 1fr))` } }}>
    {children}
  </Box>
);

export const ChartCard: React.FC<{ title: string; subtitle?: string; height?: number; children: React.ReactNode; legend?: React.ReactNode; testId?: string }> = ({ title, subtitle, height = 240, children, legend, testId }) => (
  <Card variant="outlined" data-testid={testId}>
    <CardContent sx={{ pb: '8px !important' }}>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary" component="div">{subtitle}</Typography>}
      {legend}
      <Box sx={{ height, mt: 1 }}>
        <ResponsiveContainer width="100%" height="100%">{children as any}</ResponsiveContainer>
      </Box>
    </CardContent>
  </Card>
);

/** One legend entry: a 2px line swatch + the series name in text ink (never the series colour). */
export const SeriesKey: React.FC<{ color: string; label: string; dashed?: boolean; wrap?: boolean }> = ({ color, label, dashed, wrap }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
    <Box sx={{ width: 16, height: 0, borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}`, flexShrink: 0 }} />
    {label && <Typography variant="caption" color="text.secondary" noWrap={!wrap} sx={wrap ? { color: 'text.primary', fontSize: 'inherit' } : undefined}>{label}</Typography>}
  </Stack>
);

export const seriesColor = (palette: string[], slot: number): string => palette[Math.max(0, slot) % palette.length];

/** Recharts styling shared by every simulator chart (recessive axes, solid hairline grid). */
export function useChartChrome() {
  const { C } = useAppTheme();
  const theme = useTheme();
  return {
    C,
    axis: { stroke: C.chartGrid, tick: { fill: theme.palette.text.secondary, fontSize: 11 }, tickLine: false },
    grid: { stroke: C.chartGrid, strokeOpacity: 0.6, vertical: false },
    tooltip: {
      contentStyle: { background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}`, borderRadius: 8, fontSize: 12, color: theme.palette.text.primary },
      labelStyle: { color: theme.palette.text.secondary, fontWeight: 700 },
      itemStyle: { color: theme.palette.text.primary },
    },
  };
}

/** The engine's family letters (backend adjust/types.ts FAMILY_LETTER). */
const LETTER_HINT: Record<string, string> = {
  A: 'hold', B: 'close everything', C: 'close the threatened side', D: 'reduce the threatened side', E: 'buy protection',
  F: 'roll the untested side in', G: 'video rule after a fall', H: 'video rule after a rise', I: 'add wings',
  J: 'roll the strike', K: 'roll out in time', L: 'perp delta hedge', M: 'combination of two actions',
  N: 'restructure (butterfly, calendar, delta reset, cut to defined risk, book longs)',
};

/** Ranked alternatives: a table on desktop, stacked cards on a phone. USD values shown in ₹ at `fx`. */
export const AltTable: React.FC<{ rows: SimAltRow[]; fx: number; chosenId?: string; highlight?: string[]; max?: number; testId?: string }> = ({ rows, fx, chosenId, highlight = [], max = 40, testId }) => {
  const { C } = useAppTheme();
  const phone = useIsPhone();
  const list = rows.slice(0, max);
  const tone = (v: number) => (v > 0 ? C.bull : v < 0 ? C.bear : undefined);
  const tag = (r: SimAltRow) => (r.id === chosenId ? 'chosen' : highlight.includes(r.id) ? (r.letter === 'A' ? 'hold' : 'close') : null);
  if (phone) {
    return (
      <Stack spacing={0.75} data-testid={testId}>
        {list.map(r => (
          <Box key={r.id} sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: r.id === chosenId ? 'primary.main' : 'divider', opacity: r.feasible ? 1 : 0.65 }}>
            <Stack direction="row" spacing={0.75} alignItems="flex-start" justifyContent="space-between">
              <Typography variant="caption" fontWeight={800} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{r.letter} · {r.label}</Typography>
              {tag(r) && <Chip size="small" label={tag(r)} color={tag(r) === 'chosen' ? 'primary' : 'default'} sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }} />}
            </Stack>
            {!r.feasible && <Typography variant="caption" color="text.secondary" component="div">Not feasible: {r.why}</Typography>}
            {r.feasible && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.5, mt: 0.5 }}>
                <MiniCell k="Cost" v={money(r.costUsd, fx)} />
                <MiniCell k="EV" v={signedMoney(r.evUsd, fx)} color={tone(r.evUsd)} />
                <MiniCell k="Score" v={num(r.score, 2)} />
                <MiniCell k="ES99" v={signedMoney(r.es99Usd, fx)} color={tone(r.es99Usd)} />
                <MiniCell k="Stress" v={signedMoney(r.stressUsd, fx)} color={tone(r.stressUsd)} />
                <MiniCell k="Margin" v={r.utilPct == null ? money(r.imUsd, fx) : pct(r.utilPct, 1)} />
              </Box>
            )}
            {r.breaches.length > 0 && <Typography variant="caption" sx={{ color: C.warn }} component="div">Breaches: {r.breaches.join(', ')}</Typography>}
          </Box>
        ))}
      </Stack>
    );
  }
  const H: Array<[string, string]> = [
    ['Action', 'The candidate adjustment (letter = family).'],
    ['Cost', 'Fees + modelled slippage to do it now.'],
    ['EV', 'Expected P&L over the horizon after costs, across the scenarios.'],
    ['ES99', 'Average of the worst 1% of scenario outcomes (negative = loss).'],
    ['Stress', 'Worst of the stress grid (±30% spot, IV shocks).'],
    ['Margin', 'Initial margin after the action (and use of capital).'],
    ['Δ BTC', 'Net delta after the action, in BTC.'],
    ['ΔRisk', '0.6·ΔES99 + 0.4·ΔStress versus holding (positive = less risk).'],
    ['Give-up', 'Cost + expected P&L forgone versus holding.'],
    ['Score', 'ΔRisk per ₹ of give-up (higher is better; ≤ 0 is never chosen).'],
  ];
  return (
    <Box sx={{ overflowX: 'auto' }} data-testid={testId}>
      <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.4, fontSize: '0.72rem', whiteSpace: 'nowrap' }, '& td': { fontVariantNumeric: 'tabular-nums' } }}>
        <TableHead>
          <TableRow>
            {H.map(([h, hint]) => (
              <Tooltip key={h} title={hint} arrow><TableCell sx={{ fontWeight: 700 }} align={h === 'Action' ? 'left' : 'right'}>{h}</TableCell></Tooltip>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map(r => (
            <TableRow key={r.id} selected={r.id === chosenId} sx={{ opacity: r.feasible ? 1 : 0.6 }}>
              <TableCell sx={{ minWidth: 210, maxWidth: 340, whiteSpace: 'normal !important' }}>
                <Tooltip title={`${r.letter}: ${LETTER_HINT[r.letter] || ''}${r.liquidity ? ` · ${r.liquidity}` : ''}${r.why ? ` · ${r.why}` : ''}`} arrow>
                  <Box component="span" sx={{ fontWeight: r.id === chosenId ? 800 : 600 }}>{r.letter} · {r.label}</Box>
                </Tooltip>
                {tag(r) && <Chip size="small" label={tag(r)} color={tag(r) === 'chosen' ? 'primary' : 'default'} sx={{ ml: 0.75, height: 16, fontSize: '0.58rem' }} />}
                {!r.feasible && <Typography variant="caption" color="text.secondary" component="div">Not feasible: {r.why}</Typography>}
                {r.breaches.length > 0 && <Typography variant="caption" sx={{ color: C.warn }} component="div">Breaches: {r.breaches.join(', ')}</Typography>}
              </TableCell>
              <TableCell align="right">{r.feasible ? money(r.costUsd, fx) : '—'}</TableCell>
              <TableCell align="right" sx={{ color: r.feasible ? tone(r.evUsd) : undefined }}>{r.feasible ? signedMoney(r.evUsd, fx) : '—'}</TableCell>
              <TableCell align="right" sx={{ color: r.feasible ? tone(r.es99Usd) : undefined }}>{r.feasible ? signedMoney(r.es99Usd, fx) : '—'}</TableCell>
              <TableCell align="right" sx={{ color: r.feasible ? tone(r.stressUsd) : undefined }}>{r.feasible ? signedMoney(r.stressUsd, fx) : '—'}</TableCell>
              <TableCell align="right">{r.feasible ? `${money(r.imUsd, fx)}${r.utilPct == null ? '' : ` · ${pct(r.utilPct, 1)}`}` : '—'}</TableCell>
              <TableCell align="right">{r.feasible ? num(r.delta, 3) : '—'}</TableCell>
              <TableCell align="right">{r.feasible ? signedMoney(r.dRisk, fx) : '—'}</TableCell>
              <TableCell align="right">{r.feasible ? money(r.giveUp, fx) : '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{r.feasible && r.score > -1e8 ? num(r.score, 2) : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const MiniCell: React.FC<{ k: string; v: string; color?: string }> = ({ k, v, color }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontSize: '0.62rem' }}>{k}</Typography>
    <Typography variant="caption" fontWeight={700} component="div" noWrap sx={{ color, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
  </Box>
);
